import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
// Hooks
import { useCheckAPI } from "../hooks/useCheckAPI";
import { useProtectedContent } from "../hooks/useProtectedContent";
// State Import
import { useUserBranches } from "../state/userBranchListState";
// Component Imports
// MUI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export const StaffTableSearchBar = ({
  myBranchesOnly,
  staffFilter,
  setStaffFilter,
}) => {
  const [currentSearchValue, setCurrentSearchValue] = useState(
    staffFilter.search || "",
  );
  const queryClient = useQueryClient();

  // Hoisted out of the JSX below: called inline it sat inside a conditionally
  // rendered branch, so it only ran on some renders.
  const allowAllBranches = useProtectedContent("staffAllBranches");

  // Branches
  const [userBranches, setUserBranches] = useState([]);

  // Fetch user branches
  const { data, isSuccess } = useUserBranches();

  // Do this on fetch main data success
  const successFunc = () => {
    setUserBranches(data?.data.data.data.list.branch);
  };

  // Do this on fetch main data error
  const errorFunc = () => {
    return;
  };

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.status, successFunc, errorFunc);

  // Set to filter based on myBranches flag
  useEffect(() => {
    if (myBranchesOnly) {
      setStaffFilter((prevState) => ({
        ...prevState,
        branchFilter: "Mine",
      }));
    }
  }, [myBranchesOnly]);

  const handleBranchFilterChange = (e) => {
    setStaffFilter((prevState) => ({
      ...prevState,
      branchFilter: e.target.value,
    }));
    invalidateStaffQuery();
  };

  const handleMyBranchesFilterChange = (e) => {
    setStaffFilter((prevState) => ({
      ...prevState,
      myBranchesFilter: e.target.value,
    }));
    invalidateStaffQuery();
  };

  const handleSearchChange = (newValue) => {
    setCurrentSearchValue(newValue.target.value);
  };

  const handleSearchSumbit = (e) => {
    e.preventDefault();
    setStaffFilter((prevState) => ({
      ...prevState,
      search: currentSearchValue,
    }));
    invalidateStaffQuery();
  };

  const handleSearchClear = () => {
    setCurrentSearchValue("");
    setStaffFilter((prevState) => ({
      ...prevState,
      search: "",
    }));
    invalidateStaffQuery();
  };

  const invalidateStaffQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ["staffListState"],
      exact: true,
    });
  };

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 1 }}>
      <Box component="form" onSubmit={handleSearchSumbit} sx={{ flexGrow: 1 }}>
        <TextField
          onChange={handleSearchChange}
          value={currentSearchValue}
          label="Search.."
          id="staff-table-search"
          name="Search"
          variant="outlined"
          size="small"
          fullWidth
          slotProps={{
            input: {
              endAdornment: (
                <>
                  <InputAdornment
                    position="end"
                    sx={{
                      visibility: currentSearchValue ? "visible" : "hidden",
                    }}
                  >
                    <IconButton onClick={handleSearchClear} color="error">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                  <InputAdornment position="end">
                    <IconButton type="submit">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            },
          }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ minWidth: { md: 400 } }}>
        {!myBranchesOnly && (
          <TextField
            onChange={handleBranchFilterChange}
            id="branch-filter"
            select
            label="Branch Filter"
            value={staffFilter.branchFilter}
            size="small"
            fullWidth
            disabled={myBranchesOnly}
          >
            <MenuItem value={"Mine"}>My Branches</MenuItem>
            <MenuItem value={"All"} disabled={!allowAllBranches}>
              All Branches
            </MenuItem>
          </TextField>
        )}
        <TextField
          onChange={handleMyBranchesFilterChange}
          id="my-branches-filter"
          select
          label="My Branches"
          value={staffFilter.myBranchesFilter}
          disabled={staffFilter.branchFilter === "All" ? true : false}
          size="small"
          fullWidth
        >
          <MenuItem value={"All"} sx={{ fontWeight: "medium" }}>
            All
          </MenuItem>
          {userBranches?.map((branch) => (
            <MenuItem
              key={branch.no}
              value={branch.no}
              sx={{
                pl: Number(branch.indentation) + 2,
                fontWeight: !Number(branch.indentation) && "medium",
              }}
            >
              {branch.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Stack>
  );
};
